import type { Appointment, QueueEntry } from '@/types/models';

/**
 * Regra de "próximo a ser atendido" — centralizada aqui pra Agenda,
 * gaveta do dia e sidebar usarem a MESMA lógica.
 *
 * Ordem de prioridade:
 *  1. MANUAL (`isNextUp`) — sticky, vence tudo. Se a recepção marcou alguém
 *     na mão, respeita-se a escolha.
 *  2. DILATAÇÃO PRONTA — quem foi mandado dilatar (`attendancePausedForDilation`)
 *     fura a fila, MAS só vira o próximo depois que o médico inicia o
 *     atendimento de OUTRO paciente após a dilatação (até lá a pupila ainda
 *     está dilatando, então ele não concorre). Entre vários, o que foi
 *     dilatar primeiro.
 *  3. URGÊNCIA — entre os que já estão AGUARDANDO na clínica, quem tem
 *     `priority === 'URGENCIA'` fura a fila dos eletivos (mas não vence
 *     marcação manual nem dilatação pronta). Mesmo critério de ordenação
 *     da ordem normal abaixo.
 *  4. ORDEM NORMAL — entre os que já estão AGUARDANDO na clínica: menor
 *     horário agendado; encaixe (`isOverbooking`) ordena pela hora de chegada;
 *     empate desempata por ordem de chegada.
 *
 * Tudo derivado dos agendamentos em memória — recalcula sozinho a cada render
 * (ao concluir/iniciar atendimento, marcar chegada, etc.). Não grava nada.
 * Devolve o id do agendamento próximo ou `null` se ninguém se qualifica.
 */

/** Status que tiram o paciente da disputa de "próximo" de vez. */
const TERMINAL: ReadonlySet<Appointment['status']> = new Set([
  'CANCELADA',
  'CONCLUIDA',
  'FALTOU',
]);

export interface NextUpContext {
  /** appointmentId → algo truthy quando o paciente já foi CHAMADO. Já está
   *  sendo acionado, então sai da disputa de "próximo". */
  calledByAppointmentId?: Record<string, unknown>;
  /** appointmentId → epoch ms de chegada. Fallback do `checkedInAt` do
   *  próprio doc — útil quando a fila marcou a chegada em tempo real antes
   *  do refetch gravar no agendamento. */
  arrivedAtByAppointmentId?: Record<string, number>;
}

/** Epoch ms do horário agendado (date + startTime "HH:MM"). */
function scheduledMs(a: Appointment): number {
  const [h, m] = (a.startTime || '00:00').split(':').map(Number);
  return a.date + ((h || 0) * 60 + (m || 0)) * 60_000;
}

export function pickNextUp(
  appointments: Appointment[],
  ctx: NextUpContext = {},
): string | null {
  const isCalled = (a: Appointment) => !!ctx.calledByAppointmentId?.[a.id];
  const arrivalMs = (a: Appointment): number | undefined =>
    a.checkedInAt ?? ctx.arrivedAtByAppointmentId?.[a.id];
  // "Vivo" pra disputa: fora de terminais, não em atendimento e não chamado.
  const isLive = (a: Appointment) =>
    !TERMINAL.has(a.status) && a.status !== 'EM_ATENDIMENTO' && !isCalled(a);
  // Critério de ordenação da fila "aguardando" — usado tanto pela urgência
  // (tier 3) quanto pela ordem normal (tier 4).
  const sortKey = (a: Appointment): number =>
    a.isOverbooking ? arrivalMs(a) ?? scheduledMs(a) : scheduledMs(a);
  const isWaiting = (a: Appointment) =>
    isLive(a) &&
    // Em dilatação fica fora daqui: ou já está pronta (tier 2) ou a pupila
    // ainda dilata (não concorre).
    !a.attendancePausedForDilation &&
    // Pausado porque o médico fechou a ficha: paciente segue fisicamente na
    // sala, não é "próximo" de verdade — some da disputa até a ficha reabrir.
    !a.attendancePausedForClose &&
    // "Aguardando na clínica": status EM_ESPERA ou chegada carimbada.
    (a.status === 'EM_ESPERA' || arrivalMs(a) != null);

  // 1) Manual sticky.
  const manual = appointments.find((a) => a.isNextUp && isLive(a));
  if (manual) return manual.id;

  // 2) Dilatação pronta. Comparamos com o MAIOR attendanceStartedAt da lista:
  //    é o "filler" que o médico começou a atender (em curso ou já concluído —
  //    o timestamp não some). Como pro próprio paciente vale sempre
  //    attendanceStartedAt <= dilationPausedAt, o máximo global só destrava
  //    quando veio de OUTRO paciente iniciado depois da dilatação.
  let maxStartedAt = 0;
  for (const a of appointments) {
    if (a.attendanceStartedAt && a.attendanceStartedAt > maxStartedAt) {
      maxStartedAt = a.attendanceStartedAt;
    }
  }
  const dilationReady = appointments
    .filter(
      (a) =>
        a.attendancePausedForDilation === true &&
        isLive(a) &&
        a.dilationPausedAt != null &&
        maxStartedAt > a.dilationPausedAt,
    )
    .sort((x, y) => (x.dilationPausedAt ?? 0) - (y.dilationPausedAt ?? 0));
  if (dilationReady.length > 0) return dilationReady[0].id;

  const byQueueOrder = (x: Appointment, y: Appointment) => {
    const k = sortKey(x) - sortKey(y);
    if (k !== 0) return k;
    // Desempate: ordem de chegada.
    return (arrivalMs(x) ?? Infinity) - (arrivalMs(y) ?? Infinity);
  };

  // 3) Urgência — fura a fila dos eletivos aguardando.
  const urgent = appointments
    .filter((a) => a.priority === 'URGENCIA' && isWaiting(a))
    .sort(byQueueOrder);
  if (urgent.length > 0) return urgent[0].id;

  // 4) Ordem normal — só quem já está aguardando na clínica.
  const waiting = appointments.filter(isWaiting).sort(byQueueOrder);

  return waiting[0]?.id ?? null;
}

/** Chamado pelo próprio médico há até isso, e ainda sem atendimento iniciado:
 *  é quem está entrando pela porta (ms). */
export const CHAMADA_RECENTE_MS = 10 * 60_000;

/**
 * Quem o "Atlas, abre o próximo" abre (spec 011), entre os agendamentos de
 * HOJE (o aviso pendente da recepção, quando há, vem antes — fica no
 * `NextPatientWatcher`):
 *  1. o paciente que ESTE médico chamou há pouco (`CHAMADA_RECENTE_MS`) e
 *     ainda não começou a ser atendido — chamar tira o paciente do cartão
 *     PRÓXIMO do painel, mas é ele quem vai entrar; o chamado mais recente
 *     vence;
 *  2. senão, o mesmo paciente do cartão PRÓXIMO do painel (`pickNextUp`:
 *     estrela, dilatação pronta, urgência, ordem de chegada).
 * Só agendamentos deste médico (ou sem médico) e com ficha (`patientId`).
 */
export function pickNextToOpen(
  appointments: Appointment[],
  queueEntries: Pick<QueueEntry, 'appointmentId' | 'status' | 'calledAt' | 'calledBy'>[],
  professionalId: string,
  now: number = Date.now(),
): Appointment | null {
  const meus = appointments.filter(
    (a) =>
      !a.isDeleted &&
      !!a.patientId &&
      (!a.professionalId || a.professionalId === professionalId),
  );
  const chamada: Record<string, (typeof queueEntries)[number]> = {};
  for (const e of queueEntries) {
    if (e.appointmentId && e.status === 'CALLED') chamada[e.appointmentId] = e;
  }

  // 1) Chamado agora há pouco por este médico.
  const entrando = meus
    .filter((a) => {
      const e = chamada[a.id];
      return (
        !!e &&
        e.calledBy === professionalId &&
        e.calledAt != null &&
        now - e.calledAt <= CHAMADA_RECENTE_MS &&
        !TERMINAL.has(a.status) &&
        a.status !== 'EM_ATENDIMENTO'
      );
    })
    .sort((x, y) => (chamada[y.id].calledAt ?? 0) - (chamada[x.id].calledAt ?? 0));
  if (entrando.length > 0) return entrando[0];

  // 2) O cartão PRÓXIMO do painel.
  const id = pickNextUp(meus, { calledByAppointmentId: chamada });
  return id ? meus.find((a) => a.id === id) ?? null : null;
}
